import React, { useState, useEffect, useCallback } from 'react';
import {
  Pause,
  // DollarSign,
  ShoppingCart,
  RefreshCcw,
  Repeat,
  // Trash,
  ShoppingBag,
} from 'feather-icons-react/build/IconComponents';
import { apiClient } from '../../services/apiClient';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveOrder, setSelectedCustomer, setCurrentCustomer } from '../../core/redux/action';
import HoldModal from '../../core/modals/sales/HoldModal';
import OrdersModal from '../../core/modals/sales/OrdersModal';
import TransactionModal from '../../core/modals/sales/TransactionModal';
import CategoryTabs from './components/categorytabs';
import CustomerInfo from './components/customerinfo';
import OrderList from './components/orderlist';
import PaymentMethods from './components/paymentmethods';
import PaymentSummary from './components/paymentsummary';
import ProductList from './components/productlist';
import '../../style/css/newPos.css';
import { useToast } from '../../Toaster/ToastContext';
import ReceiptModal from '../../core/modals/sales/ReceiptModal';
import CustomerModal from '../../core/modals/peoples/customerModal';

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const Pos = () => {
  const [storeInfo] = useState({
    name: 'Dreamguys Technologies Pvt Ltd.',
    address: '123 Business St, City, Country',
    phone: '(123) 456-7890',
    logo: '/assets/img/logo.svg',
    website: 'www.dreamguys.com',
  });
  const { showToast } = useToast();
  const [state, setState] = useState({
    categories: [],
    brands: [],
    products: [],
    customers: [],
    activeTab: 'all',
    selectedBrand: '',
    loading: true,
    error: null,
    orderItems: [],
    isDragging: false,
    isHoldModalOpen: false,
    isOrdersModalOpen: false,
    isTransactionModalOpen: false,
    searchQuery: '',
  });

  const [tax] = useState(0);
  const dispatch = useDispatch();
  const selectedCustomer = useSelector(state => state.selectedCustomer);
  const selectedBranch = useSelector((state) => state.selectedBranch);
  const activeOrder = useSelector((state) => state.activeOrder);
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [payments, setPayments] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const handleAddPayment = (payment) => {
    // console.log(payment);
    setPayments({ payment });
  };
  
  const handleAddCustomer = () => {
    dispatch(setCurrentCustomer(null));
    setShowCustomerModal(true);
  };

  const handleCustomerModalClose = () => {
    setShowCustomerModal(false);
  };

  const handleCustomerOperationComplete = () => {
    // Refresh customer list after adding/updating a customer
    fetchData();
  };

  const [localUserData] = useState({
    name: capitalizeFirstLetter(localStorage.getItem("name") || "Guest"),
  });

  const fetchData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      dispatch(setSelectedCustomer(''));

      const data = { branch_id: selectedBranch?.id || 13
       };

      const [categoryResponse, brandResponse, customerResponse, productResponse] = await Promise.all([
        apiClient.get('/get-all-category'),
        apiClient.get('/get-all-brand'),
        apiClient.get('/get-all-customer'),
        apiClient.post('/get-all-products', data),
      ]);

      const customers = customerResponse.data?.map(customer => ({
        value: customer.id.toString(),
        label: customer.customer_name || 'Unknown',
        bonus: customer.bonus || 0,
        loyalty: customer.loyalty || 0,
      })) || [];

      const flattenProducts = (products) => {
        const flat = [];
        products.forEach(product => {
          if (product.product_type === 'variable' && Array.isArray(product.variants)) {
            product.variants.forEach(variant => {
              flat.push({
                ...product,
                id: `${product.id}-${variant.id}`,
                name: `${product.name} (${variant.variant_value})`,
                sku: variant.sku,
                quantity: parseInt(variant.quantity, 10) || 0,
                price: parseFloat(variant.price) || 0,
                discount_type: variant.discount_type || null,
                discount_value: parseFloat(variant.discount_value) || 0,
                variant_id: variant.id,
                is_variant: true,
              });
            });
          } else {
            flat.push({
              ...product,
              price: parseFloat(product.price) || 0,
              quantity: parseInt(product.quantity, 10) || 0,
              is_variant: false,
              discount_type: product.discount_type || null,
              discount_value: parseFloat(product.discount_value) || 0,
            });
          }
        });
        return flat;
      };

      const flattenedProducts = flattenProducts(productResponse.data || []);

      const brands = brandResponse.data?.map(brand => ({
        value: brand.id.toString(),
        label: brand.name || 'Unknown',
      })) || [];

      setState(prev => ({
        ...prev,
        categories: categoryResponse.data || [],
        brands: [{ value: '', label: 'All Brands' }, ...brands],
        customers: [{ value: '', label: 'Choose' }, ...customers],
        products: flattenedProducts,
        loading: false,
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  }, [dispatch, selectedBranch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedCustomer && state.customers.length > 1 && !state.customers.find(c => c.value === String(selectedCustomer))) {
      dispatch(setSelectedCustomer(''));
    }
  }, [selectedCustomer, state.customers, dispatch]);

  const handleTabClick = useCallback((tabId) => {
    setState(prev => ({ ...prev, activeTab: tabId }));
  }, []);

  const handleBrandChange = useCallback((e) => {
    setState(prev => ({ ...prev, selectedBrand: e.target.value }));
  }, []);

  const getCategoryName = useCallback((categoryId) => {
    if (!categoryId) return 'Uncategorized';
    const category = state.categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  }, [state.categories]);

  const getBrandName = useCallback((brandId) => {
    if (!brandId) return 'No Brand';
    const brand = state.brands.find(b => b.value === brandId.toString());
    return brand ? brand.label : 'No Brand';
  }, [state.brands]);

  const handleDragStart = (e, product) => {
    e.dataTransfer.setData('product', JSON.stringify({ ...product, quantity: 1 }));
    setState(prev => ({ ...prev, isDragging: true }));
  };

  const handleDragEnd = () => {
    setState(prev => ({ ...prev, isDragging: false }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isDragging: true }));
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isDragging: false }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isDragging: false }));
    try {
      const product = JSON.parse(e.dataTransfer.getData('product'));
      addToOrder(product, product.quantity || 1);
    } catch (err) {
      console.error('Error parsing dragged product:', err);
      showToast('Failed to add product to order', 'error', 'POS');
    }
  };

  const addToOrder = (product, quantity = 1) => {
    const price = parseFloat(product.price) || 0;
    const name = product.name;
    const discountType = product.discount_type || null;
    const discountValue = parseFloat(product.discount_value) || 0;
    let discountedPrice = price;
    let discountAmount = 0;

    if (discountType === 'percentage' && discountValue > 0) {
      discountAmount = (price * discountValue) / 100;
      discountedPrice = price - discountAmount;
    } else if (discountType === 'fixed' && discountValue > 0) {
      discountAmount = discountValue;
      discountedPrice = price - discountAmount;
    }

    if (isNaN(price) || price <= 0) {
      showToast(`Invalid price for ${name}`, 'error', 'POS');
      return;
    }

    if (product.quantity !== null && product.quantity < quantity) {
      showToast(`Insufficient stock for ${name} (${product.quantity} available)`, 'error', 'POS');
      return;
    }

    setState(prev => {
      const existingItem = prev.orderItems.find(item =>
        item.id === product.id && (!product.is_variant || item.variantId === product.variant_id)
      );

      if (existingItem) {
        return {
          ...prev,
          orderItems: prev.orderItems.map(item =>
            item.id === product.id && (!product.is_variant || item.variantId === product.variant_id)
              ? { ...item, quantity: item.quantity + (quantity || 1) }
              : item
          ),
        };
      }

      return {
        ...prev,
        orderItems: [
          ...prev.orderItems,
          {
            id: product.id,
            variantId: product.is_variant ? product.variant_id : null,
            name,
            price,
            discountedPrice,
            discountAmount,
            discountType,
            discountValue,
            quantity: quantity || 1,
          },
        ],
      };
    });
  };

  const handleRemoveItem = (id, variantId) => {
    setState(prev => ({
      ...prev,
      orderItems: prev.orderItems.filter(item =>
        !(item.id === id && (!variantId || item.variantId === variantId))
      ),
    }));
  };

  const handleQuantityChange = (id, variantId, delta) => {
    setState(prev => ({
      ...prev,
      orderItems: prev.orderItems.map(item =>
        item.id === id && (!variantId || item.variantId === variantId)
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      ),
    }));
  };

  const handleHoldModalToggle = () => setState(prev => ({ ...prev, isHoldModalOpen: !prev.isHoldModalOpen }));
  const handleOrdersModalToggle = () => setState(prev => ({ ...prev, isOrdersModalOpen: !prev.isOrdersModalOpen }));
  const handleTransactionModalToggle = () => setState(prev => ({ ...prev, isTransactionModalOpen: !prev.isTransactionModalOpen }));

  const totals = useCallback(() => {
    const subtotal = state.orderItems.reduce(
      (sum, item) => sum + (item.discountedPrice || item.price) * (item.quantity ?? 1),
      0
    );
    const totalDiscount = state.orderItems.reduce(
      (sum, item) => sum + (item.discountAmount || 0) * (item.quantity ?? 1),
      0
    );

    if (state.orderItems.length === 0) {
      return {
        subtotal: 0,
        totalDiscount: 0,
        total: 0,
      };
    }

    const total = subtotal + tax;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      totalDiscount: parseFloat(totalDiscount.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
    };
  }, [state.orderItems, tax]);

  const clearOrder = () => {
    setState(prev => ({ ...prev, orderItems: [] }));
    setPayments(null); // Reset payments when clearing order
  };

  const handlePlaceOrder = async () => {
    if (state.orderItems.length === 0) {
      showToast('No items in order.', 'info', 'POS');
      return;
    }

    const { method, amount } = payments?.payment || {};

    if (!method || !amount || amount <= 0) {
      showToast('Please select a payment method and enter a valid amount.', 'error', 'POS');
      return;
    }

    setIsPlacingOrder(true);

    const customerId = selectedCustomer || null;

    const orderPayload = {
      branch_id: selectedBranch?.id,
      customer_id: customerId,
      status: 'completed',
      items: state.orderItems.map(item => ({
        product_id: String(item.id).split('-')[0],
        quantity: item.quantity ?? 1,
        price: item.discountedPrice || item.price,
        variant_id: item.variantId || null,
      })),
      payment: {
        method,
        amount: parseFloat(amount),
      },
      note: `Order placed on ${new Date().toLocaleString()}`,
      ...(activeOrder?.id && { sale_id: activeOrder.id }),
    };

    try {
      const response = await apiClient.post('/store-sale', orderPayload);
      console.log('API response:', response); // Debug the full response
      if (response.status) {
        showToast(response.message || 'Order placed successfully!', 'success', 'POS');
        setLastOrder(response.sale); // 👈 store order for printing
        setShowReceipt(true);        // 👈 open receipt modal
        clearOrder();
        dispatch(setSelectedCustomer(''));
        dispatch(setActiveOrder(null));
      }
      else {
        showToast('Order could not be placed. Please try again.', 'error', 'POS');
        console.warn('Unexpected response:', response);
      }
    } catch (err) {
      console.error('Failed to place order:', err);
      let message = 'Failed to place order. Try again.';
      if (!err?.status) {
        const errorMessages = Object.values(err.response?.data?.errors || {}).flat().join(' ');
        message = errorMessages || err.response?.data?.message || message;
      } else if (err?.message) {
        message = err.message;
      }
      showToast(message, 'error', 'POS');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const filteredProducts = state.products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(state.searchQuery.toLowerCase());
    const matchesCategory = state.activeTab === 'all' || 
                           getCategoryName(product.category_id).toLowerCase() === state.activeTab;
    const matchesBrand = state.selectedBrand === '' || product.brand_id?.toString() === state.selectedBrand;
    return matchesSearch && matchesCategory && matchesBrand;
  });

  useEffect(() => {
    if (activeOrder && Array.isArray(activeOrder.items)) {
      const newOrderItems = activeOrder.items.map((item) => {
        const price = parseFloat(item.price) || 0;
        const discountType = item.discount_type || null;
        const discountValue = parseFloat(item.discount_value) || 0;
        let discountedPrice = price;
        let discountAmount = 0;

        if (discountType === 'percentage' && discountValue > 0) {
          discountAmount = (price * discountValue) / 100;
          discountedPrice = price - discountAmount;
        } else if (discountType === 'fixed' && discountValue > 0) {
          discountAmount = discountValue;
          discountedPrice = price - discountAmount;
        }

        return {
          id: item.variant_id ? `${item.product_id}-${item.variant_id}` : item.product_id,
          name: item.variant_value
            ? `${item.product_name} (${item.variant_value})`
            : item.product_name,
          price,
          quantity: parseInt(item.quantity, 10) || 1,
          variantId: item.variant_id || null,
          discount_type: discountType,
          discount_value: discountValue,
          discountedPrice,
          discountAmount,
        };
      });

      setState((prev) => ({
        ...prev,
        orderItems: newOrderItems,
      }));

      if (activeOrder.customer_id) {
        const customerId = activeOrder.customer_id.toString();
        const customerExists = state.customers.find((c) => c.value === customerId);
        if (customerExists) {
          dispatch(setSelectedCustomer(customerId));
        } else {
          showToast('Customer not found for this order', 'error', 'POS');
        }
      } else {
        dispatch(setSelectedCustomer(''));
      }
    } else if (activeOrder) {
      console.error('Invalid activeOrder data:', activeOrder);
      showToast('Invalid order data', 'error', 'POS');
    }
  }, [activeOrder, dispatch, state.customers, showToast]);

  const handleSearchChange = (e) => {
    setState(prev => ({ ...prev, searchQuery: e.target.value }));
  };

  return (
    <div className="pos-page">
      <div className="main-wrapper pos-five">
        <div className="page-wrapper pos-pg-wrapper ms-0">
          <div className="content pos-design p-0">
            <div className="row pos-wrapper">
              <div className="col-md-12 col-lg-7 col-xl-8 d-flex">
                <div className="pos-categories p-0 flex-fill">
                  <div className="content-wrap">
                    <div className="tab-wrap">
                      <CategoryTabs
                        categories={state.categories}
                        activeTab={state.activeTab}
                        onTabClick={handleTabClick}
                      />
                    </div>
                    <div className="tab-content-wrap">
                      <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <div className="mb-3">
                          <h5>Welcome, {capitalizeFirstLetter(localUserData.name)}</h5>
                          <p>{new Date().toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}</p>
                        </div>
                        <div className="d-flex align-items-center flex-wrap mb-2">
                          <div className="input-icon-start search-pos position-relative mb-2 me-3">
                            <span className="input-icon-addon">
                              <i className="ti ti-search"></i>
                            </span>
                            <input type="text" className="form-control" placeholder="Search Product" onChange={handleSearchChange}
                            value={state.searchQuery} />


                          </div>
                          <div className="mb-2 me-3">
                            <select 
                              className="form-select form-select-sm" 
                              value={state.selectedBrand} 
                              onChange={handleBrandChange}
                              aria-label="Select Brand"
                            >
                              {state.brands.map(brand => (
                                <option key={brand.value} value={brand.value}>{brand.label}</option>
                              ))}
                            </select>
                          </div>
                          {/* <a href="#" className="btn btn-sm btn-primary mb-2"><i className="ti ti-star me-1"></i>Featured</a> */}
                        </div>
                      </div>
                      <div className="pos-products">
                        <div className="tabs_container">
                          <ProductList
                            products={filteredProducts.map(product => ({
                              ...product,
                              price: typeof product.price === 'string' ? parseFloat(product.price) : product.price,
                            }))}
                            activeTab={state.activeTab}
                            getCategoryName={getCategoryName}
                            getBrandName={getBrandName}
                            loading={state.loading}
                            error={state.error}
                            handleDragStart={handleDragStart}
                            handleDragEnd={handleDragEnd}
                            handleAddToOrder={(product, quantity) => addToOrder(product, quantity || 1)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`col-md-12 col-lg-5 col-xl-4 ps-0 theiaStickySidebar d-lg-flex ${state.isDragging ? 'drop-zone-active' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <aside className="product-order-list bg-secondary-transparent flex-fill">
                  <div className="card">
                    <div className="card-body">
                      <div className="order-head d-flex align-items-center justify-content-between w-100">
                        <div>
                          <h3>Order List</h3>
                        </div>
                      </div>
                      <CustomerInfo
                        customers={state.customers}
                        selectedCustomer={selectedCustomer}
                        dispatch={dispatch}
                        onAddCustomer={handleAddCustomer}
                      />
                      <OrderList
                        orderItems={state.orderItems}
                        handleRemoveItem={handleRemoveItem}
                        handleQuantityChange={handleQuantityChange}
                        isDragging={state.isDragging}
                        setOrderItems={(items) => setState(prev => ({ ...prev, orderItems: items }))}
                      />
                      <PaymentSummary
                        totals={totals()}
                        tax={tax}
                      />
                    </div>
                  </div>
                  {state.orderItems.length > 0 && (
                    <PaymentMethods onAddPayment={handleAddPayment} />
                  )}
                  <div className="btn-row d-flex align-items-center justify-content-between gap-3">
                    {/* <button
                      className="btn btn-white d-flex align-items-center justify-content-center flex-fill m-0"
                      onClick={() => {
                        const dummyOrder = {
                          customer_id: selectedCustomer || null,
                          items: state.orderItems,
                          payment: {
                            method: 'cash',
                            amount: totals().total,
                          },
                          status: 'draft',
                          note: 'Print preview',
                          created_at: new Date().toISOString(),
                        };
                        showReceiptAndPrint(dummyOrder);
                      }}
                      disabled={state.orderItems.length === 0 || isPlacingOrder}
                    >
                      <Printer size={14} className="me-2" /> Print Order
                    </button> */}
                    <button
                      className="btn btn-secondary d-flex align-items-center justify-content-center flex-fill m-0"
                      onClick={handlePlaceOrder}
                      disabled={state.orderItems.length === 0 || isPlacingOrder}
                    >
                      <ShoppingBag size={14} className="me-2" />
                      {isPlacingOrder ? 'Placing Order...' : 'Place Order'}
                    </button>
                  </div>
                </aside>
              </div>
            </div>
            <HoldModal
              isOpen={state.isHoldModalOpen}
              onClose={handleHoldModalToggle}
              orderItems={state.orderItems}
              clearOrder={clearOrder}
              customerId={selectedCustomer || null}
            />
            <OrdersModal
              isOpen={state.isOrdersModalOpen}
              onClose={handleOrdersModalToggle}
            />
            <TransactionModal
              isOpen={state.isTransactionModalOpen}
              onClose={handleTransactionModalToggle}
            />
            <ReceiptModal
              show={showReceipt}
              onClose={() => setShowReceipt(false)}
              orderData={lastOrder}
              storeInfo={storeInfo}
            />
            <CustomerModal
              show={showCustomerModal}
              customer={null}
              onClose={handleCustomerModalClose}
              onOperationComplete={handleCustomerOperationComplete}
            />
            <div className="pos-footer bg-white p-3 border-top">
              <div className="d-flex align-items-center justify-content-center flex-wrap gap-2">
                <button onClick={handleHoldModalToggle} className="btn btn-orange d-inline-flex align-items-center justify-content-center gap-2"><Pause /> Hold</button>
                {/* <button className="btn btn-info d-inline-flex align-items-center justify-content-center gap-2"><Trash /> Void</button> */}
                {/* <button className="btn btn-cyan d-flex align-items-center justify-content-center gap-2" data-bs-toggle="modal" data-bs-target="#payment-completed"><DollarSign /> Payment</button> */}
                <button onClick={handleOrdersModalToggle} className="btn btn-secondary d-inline-flex align-items-center justify-content-center gap-2"><ShoppingCart /> View Orders</button>
                <button onClick={clearOrder} className="btn btn-indigo d-inline-flex align-items-center justify-content-center gap-2"><RefreshCcw /> Reset</button>
                <button onClick={handleTransactionModalToggle} className="btn btn-danger d-inline-flex align-items-center justify-content-center gap-2"><Repeat /> Transaction</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pos;